import { useRef, useCallback, useState } from "react";

const MIN_WORD_LENGTH = 3;

function CopyButton({ words }: { readonly words: readonly string[] }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const passphrase = words.filter(Boolean).join(" ");
    navigator.clipboard.writeText(passphrase).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-xs text-ink-muted hover:text-ink transition-colors"
    >
      {copied ? "Copié !" : "Copier"}
    </button>
  );
}

interface PassphraseInputProps {
  readonly words: readonly string[];
  readonly onChange: (words: string[]) => void;
  readonly minWords: number;
  readonly placeholder?: string;
  readonly autoComplete?: string;
}

export function PassphraseInput({
  words,
  onChange,
  minWords,
  autoComplete = "off",
}: PassphraseInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const fieldCount = Math.max(minWords, words.length + 1);

  const setRef = useCallback((index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  }, []);

  const updateWord = (index: number, value: string) => {
    // Detect paste or autocomplete with separators
    if (value.includes(" ") || value.includes("-")) {
      const parts = value.split(/[\s-]+/).filter((w) => w.trim());
      if (parts.length > 1) {
        const updated = [...words];
        for (let i = 0; i < parts.length; i++) {
          updated[index + i] = parts[i].toLowerCase();
        }
        onChange(updated);
        // Focus the field after the last inserted word
        const nextIndex = index + parts.length;
        setTimeout(() => inputRefs.current[nextIndex]?.focus(), 0);
        return;
      }
    }

    const updated = [...words];
    const trimmed = value.trim().toLowerCase();
    updated[index] = trimmed;

    // Remove trailing empty entries
    while (updated.length > 0 && !updated[updated.length - 1]) {
      updated.pop();
    }

    onChange(updated);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const current = (e.target as HTMLInputElement).value.trim();
      if (current) {
        updateWord(index, current);
        setTimeout(() => inputRefs.current[index + 1]?.focus(), 0);
      }
    }
    if (e.key === "Backspace" && !(e.target as HTMLInputElement).value) {
      e.preventDefault();
      if (index > 0) {
        const updated = [...words];
        updated.splice(index - 1, 1);
        onChange(updated);
        setTimeout(() => {
          const prev = inputRefs.current[index - 1];
          if (prev) {
            prev.focus();
            prev.setSelectionRange(prev.value.length, prev.value.length);
          }
        }, 0);
      }
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (pasted.includes(" ") || pasted.includes("-")) {
      e.preventDefault();
      updateWord(index, pasted);
    }
  };

  const validWords = words.filter((w) => w.length >= MIN_WORD_LENGTH);
  const filledCount = validWords.length;
  const enoughWords = filledCount >= minWords;

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        {Array.from({ length: fieldCount }, (_, i) => (
          <div key={i} className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-ink-muted select-none">
              {i + 1}.
            </span>
            <input
              ref={setRef(i)}
              type="text"
              value={words[i] ?? ""}
              onChange={(e) => updateWord(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              autoComplete={autoComplete}
              spellCheck={false}
              className={`w-full pl-7 pr-3 py-2.5 bg-surface-alt border text-ink
                text-sm font-mono rounded focus:outline-none focus:ring-2 focus:ring-accent
                placeholder:text-ink-muted placeholder:font-sans ${
                  words[i] && words[i].length < MIN_WORD_LENGTH
                    ? "border-red-500/50"
                    : "border-border"
                }`}
              placeholder={i === 0 ? "mot (3 lettres min.)..." : ""}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span className={`text-xs ${enoughWords ? "text-emerald-500" : "text-ink-muted"}`}>
          {filledCount}/{minWords} mots {enoughWords ? "" : "minimum"}
        </span>
        {filledCount > 0 && <CopyButton words={words} />}
      </div>
    </div>
  );
}
